import { useCallback, useEffect } from 'preact/hooks';

import { type Wallet } from '@/shared/api/user/types';
import { BaseLogger } from '@/shared/service/logger/BaseLogger';
import { LogDevMessageKinds } from '@/shared/service/logger/types';
import { useAppActions, useAppState } from '@/shared/stores/app-store/app-store';

import { MessageType } from './types';

import { type UseWsReturnType } from '.';

export const useSubscribeToWallet = (ws: UseWsReturnType) => {
  const { wsSentInitialMsg } = useAppState();
  const { hydrate } = useAppActions();

  const walletWSHandler = useCallback(
    ({ payload: { wallets } }: { payload: { wallets: Wallet[] } }) => {
      if (import.meta.env.DEV) {
        logWalletChanges(wallets);
      }

      hydrate(({ userProfile }) => {
        if (!userProfile) return {};
        const prevWallets = userProfile?.wallets ?? [];

        const newWallets = prevWallets.map((prevWallet) => {
          const newItem = wallets.find((newItem) => newItem.type === prevWallet.type);
          return newItem ? newItem : prevWallet;
        });

        return {
          userProfile: {
            ...userProfile,
            wallets: newWallets,
          },
        };
      });
    },
    [hydrate]
  );

  useEffect(() => {
    if (!wsSentInitialMsg) return;

    const subscriptionId = ws.subscribe(MessageType.WALLET, walletWSHandler);

    return () => ws.unsubscribe(MessageType.WALLET, subscriptionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsSentInitialMsg]);
};

const logWalletChanges = BaseLogger.makeLogDevMessage<Wallet[], boolean>(
  LogDevMessageKinds.ws,
  'WALLET',
  ['Message', (wallets) => wallets]
);
